import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Plus, Globe, TrendingUp, Zap, Activity, ListTree } from "lucide-react";
import ProviderSLAChart from "@/components/admin/ProviderSLAChart";
import MoversTodayCard from "@/components/admin/MoversTodayCard";
import BeCoChainVerifierCard from "@/components/admin/BeCoChainVerifierCard";
import BeCoAuditExportCard from "@/components/admin/BeCoAuditExportCard";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

/**
 * AdminSymbols — the super-admin registry of every tradable instrument.
 *
 * Shows the `live_symbols_registry` collection, lets an admin trigger an
 * ad-hoc Mansa+FMP reconciliation, and manually add a symbol that neither
 * provider has picked up yet. Rows are grouped by country with source and
 * active-flag badges.
 */
export default function AdminSymbols() {
    const [data, setData]         = useState(null);
    const [health, setHealth]     = useState(null);
    const [loading, setLoading]   = useState(true);
    const [syncing, setSyncing]   = useState(false);
    const [pingLoading, setPingLoading] = useState(false);
    const [showAdd, setShowAdd]   = useState(false);
    const [form, setForm]         = useState({
        symbol: "", name: "", category: "equity",
        country: "KE", exchange: "NSE", seed_price: "",
    });

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/symbols");
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load symbol registry");
        } finally { setLoading(false); }
    }

    async function loadHealth() {
        setPingLoading(true);
        try {
            const { data } = await api.get("/admin/symbols/health");
            setHealth(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to ping providers");
        } finally { setPingLoading(false); }
    }

    async function triggerSync() {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/symbols/sync");
            toast.success(
                `Sync done · Mansa added ${data.mansa?.added || 0}, ` +
                `updated ${data.mansa?.updated || 0}, ` +
                `${data.tickers_hot_added || 0} hot-registered`,
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
        } finally { setSyncing(false); }
    }

    async function addManual(e) {
        e.preventDefault();
        try {
            const payload = { ...form, seed_price: Number(form.seed_price) };
            await api.post("/admin/symbols", payload);
            toast.success(`${form.symbol.toUpperCase()} registered — tradable this pod tick`);
            setShowAdd(false);
            setForm({ symbol: "", name: "", category: "equity", country: "KE", exchange: "NSE", seed_price: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        }
    }

    async function toggleActive(sym, active) {
        try {
            await api.patch(`/admin/symbols/${sym}`, { active });
            toast.success(`${sym} ${active ? "activated" : "deactivated"}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    }

    useEffect(() => { load(); loadHealth(); }, []);

    const symbols  = data?.symbols || [];
    const byCountry = symbols.reduce((acc, s) => {
        const k = s.country || "?";
        (acc[k] ||= []).push(s);
        return acc;
    }, {});

    const symbolsWidgets = [
        {
            id: "kpi-strip", title: "Registry KPIs", category: "Overview",
            layout: { x: 0, y: 0, w: 12, h: 3 }, minW: 6, minH: 2, noPadding: true,
            render: () => (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3">
                    <Card><CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Total registered</div>
                        <div className="text-3xl font-bold">{data?.total ?? "—"}</div>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Active</div>
                        <div className="text-3xl font-bold text-emerald-500">{data?.active ?? "—"}</div>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Mansa-sourced</div>
                        <div className="text-3xl font-bold flex items-center gap-2">
                            {data?.by_source?.mansa ?? "—"}
                            <Globe className="w-5 h-5 text-blue-400" />
                        </div>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Other sources</div>
                        <div className="text-3xl font-bold flex items-center gap-2">
                            {data?.by_source?.manual ?? "—"}
                            <TrendingUp className="w-5 h-5 text-red-400" />
                        </div>
                    </CardContent></Card>
                </div>
            ),
        },
        {
            id: "provider-health", title: "Provider Health", category: "Monitoring",
            layout: { x: 0, y: 3, w: 12, h: 6 }, minW: 6, minH: 4, noPadding: true,
            render: () => (
                <Card data-testid="provider-health-card" className="border-0 shadow-none">
                    <CardHeader className="flex-row items-center justify-between space-y-0">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Activity className="w-5 h-5 text-emerald-500" />
                            Provider health
                        </CardTitle>
                        <Button
                            variant="ghost" size="sm"
                            onClick={loadHealth}
                            disabled={pingLoading}
                            data-testid="provider-health-refresh"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${pingLoading ? "animate-spin" : ""}`} />
                            Re-ping
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {["mansa"].map((key) => {
                                const p = health?.providers?.[key] || {};
                                const badgeColor = p.ok
                                    ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
                                    : p.configured
                                        ? "bg-rose-500/10 text-rose-500 border-rose-500/30"
                                        : "bg-muted text-muted-foreground border-border";
                                const label = p.ok ? "Healthy" : p.configured ? "Degraded" : "Not configured";
                                return (
                                    <div key={key} className="border border-border rounded-lg p-3" data-testid={`provider-health-${key}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="font-semibold uppercase text-sm">
                                                {key === "mansa" ? "Mansa (Pan-African feeds)" : key}
                                            </div>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${badgeColor}`}>
                                                {label}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <div className="text-muted-foreground">Latency</div>
                                                <div className="font-mono font-bold">{p.latency_ms != null ? `${p.latency_ms} ms` : "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-muted-foreground">Configured</div>
                                                <div className="font-mono">{p.configured ? "yes" : "no"}</div>
                                            </div>
                                            {p.sample && (
                                                <div className="col-span-2">
                                                    <div className="text-muted-foreground">Sample quote</div>
                                                    <div className="font-mono text-xs">
                                                        {p.sample.symbol} · {p.sample.price != null ? p.sample.price : "no data"}
                                                    </div>
                                                </div>
                                            )}
                                            {p.error && (
                                                <div className="col-span-2 text-rose-400 text-[11px] break-all">
                                                    {p.error}
                                                </div>
                                            )}
                                            <div className="col-span-2">
                                                <div className="text-muted-foreground">Last seen in registry</div>
                                                <div className="font-mono text-[11px]">{p.last_seen_in_registry ? p.last_seen_in_registry.slice(0, 19).replace("T", " ") + " UTC" : "—"}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {health?.checked_at && (
                            <div className="text-[11px] text-muted-foreground mt-2 text-right">
                                Checked {new Date(health.checked_at).toLocaleTimeString()} · cache size {health.cache_size ?? 0} · Mansa universe {health.mansa_universe ?? 0}
                            </div>
                        )}
                    </CardContent>
                </Card>
            ),
        },
        {
            id: "provider-sla", title: "Provider SLA · 7 Day", category: "Monitoring",
            layout: { x: 0, y: 9, w: 12, h: 7 }, minW: 6, minH: 4, noPadding: true,
            render: () => <ProviderSLAChart />,
        },
        {
            id: "movers-today", title: "Movers Today", category: "Listings",
            layout: { x: 0, y: 16, w: 6, h: 6 }, minW: 4, minH: 4, noPadding: true,
            render: () => <MoversTodayCard />,
        },
        {
            id: "beco-chain-verifier", title: "BeCo Chain Verifier", category: "Audit",
            layout: { x: 6, y: 16, w: 6, h: 6 }, minW: 4, minH: 4, noPadding: true,
            render: () => <BeCoChainVerifierCard />,
        },
        {
            id: "beco-audit-export", title: "BeCo Audit Export", category: "Audit",
            layout: { x: 0, y: 22, w: 12, h: 5 }, minW: 6, minH: 3, noPadding: true,
            render: () => <BeCoAuditExportCard />,
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-symbols-page">
            <PageHeader
                icon={ListTree}
                eyebrow="Modules"
                title="Symbol Registry"
                subtitle='Every tradable instrument on the platform, sourced from Mansa Markets + manual admin adds. Reconciliation runs hourly; hit "Sync now" to pick up new listings immediately.'
                actions={
                    <>
                        <Button
                            variant="outline"
                            onClick={triggerSync}
                            disabled={syncing}
                            data-testid="admin-symbols-sync"
                        >
                            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                            {syncing ? "Syncing…" : "Sync now"}
                        </Button>
                        <Button onClick={() => setShowAdd((v) => !v)} data-testid="admin-symbols-add">
                            <Plus className="w-4 h-4 mr-1.5" />
                            {showAdd ? "Cancel" : "Manual add"}
                        </Button>
                    </>
                }
                testId="admin-symbols-header"
            />

            {showAdd && (
                <Card data-testid="admin-symbols-manual-form">
                    <CardHeader><CardTitle>Manual symbol registration</CardTitle></CardHeader>
                    <CardContent>
                        <form onSubmit={addManual} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Input placeholder="Symbol (SCOM)"    value={form.symbol}     onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} required />
                            <Input placeholder="Full name"        value={form.name}       onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            <Input placeholder="Seed price (KES)" type="number" step="0.01" value={form.seed_price} onChange={(e) => setForm({ ...form, seed_price: e.target.value })} required />
                            <select className="border border-border rounded px-2 py-2 bg-background" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                <option value="equity">equity</option>
                                <option value="etf">etf</option>
                                <option value="crypto">crypto</option>
                                <option value="fx">fx</option>
                            </select>
                            <Input placeholder="Country (ISO — KE)" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} required />
                            <Input placeholder="Exchange (NSE/DSE/…)" value={form.exchange} onChange={(e) => setForm({ ...form, exchange: e.target.value.toUpperCase() })} required />
                            <div className="sm:col-span-3">
                                <Button type="submit"><Plus className="w-4 h-4 mr-1.5" /> Register</Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            <DashboardGridWrapper
                role="SUPER_ADMIN"
                viewId="admin-symbols"
                widgets={symbolsWidgets}
                className="mb-3"
            />

            {loading && <div className="text-sm text-muted-foreground">Loading registry…</div>}
            {!loading && Object.keys(byCountry).sort().map((cc) => (
                <CountrySymbolsTable key={cc} cc={cc} symbols={byCountry[cc]} onToggleActive={toggleActive} />
            ))}
        </div>
    );
}

function CountrySymbolsTable({ cc, symbols, onToggleActive }) {
    const { sorted: sortedSymbols, sortKey, direction, requestSort } = useSortableData(symbols, {
        accessors: {
            symbol: (s) => s.symbol || "",
            name: (s) => s.name || "",
            exchange: (s) => s.exchange || "",
            source: (s) => s.source || "",
            seed: (s) => Number(s.seed_price || 0),
            status: (s) => s.active ? "active" : "retired",
        },
    });
    return (
        <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2">
                <span>{cc}</span>
                <Badge variant="outline">{symbols.length} symbols</Badge>
            </CardTitle></CardHeader>
            <CardContent className="overflow-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-muted-foreground border-b border-border">
                            <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-3" />
                            <SortableTh label="Name" sortKey="name" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-3" />
                            <SortableTh label="Exchange" sortKey="exchange" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-3" />
                            <SortableTh label="Source" sortKey="source" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-3" />
                            <SortableTh label="Seed KES" sortKey="seed" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-3" />
                            <SortableTh label="Status" sortKey="status" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedSymbols.map((s) => (
                            <tr key={s.symbol} className="border-b border-border/50" data-testid={`admin-symbol-row-${s.symbol}`}>
                                <td className="py-1.5 pr-3 font-mono font-semibold">{s.symbol}</td>
                                <td className="py-1.5 pr-3">{s.name}</td>
                                <td className="py-1.5 pr-3">{s.exchange}</td>
                                <td className="py-1.5 pr-3">
                                    <Badge variant={s.source === "mansa" ? "default" : "outline"}>
                                        {s.source === "manual" && <Zap className="w-3 h-3 mr-1" />}
                                        {s.source}
                                    </Badge>
                                </td>
                                <td className="py-1.5 pr-3 font-mono">{Number(s.seed_price || 0).toFixed(2)}</td>
                                <td className="py-1.5 pr-3">
                                    <button
                                        onClick={() => onToggleActive(s.symbol, !s.active)}
                                        data-testid={`admin-symbol-toggle-${s.symbol}`}
                                        className={`text-xs px-2 py-0.5 rounded border ${s.active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-muted text-muted-foreground border-border"}`}
                                    >
                                        {s.active ? "Active" : "Retired"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
}
